import usePrivateAxios from "@/common/api/usePrivateAxios";
import { defaultTransformerResponse } from "@/common/constants/defaultTransformerResponse.constant";
import { MetaResponse } from "@/common/types/metaResponse";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { BranchesEquipmentsConsumptionsTotalizerTransformer } from "../services/branchesEquipmentsConsumptionsTotalizerTransformer";
import { BranchesEquipmentsConsumptionsTotalizerData } from "../types/branchesEquipmentsConsumptionsTotalizer.type";

type UseBranchesEquipmentsConsumptionsTotalizerProps = {
  filters?: {
    page?: number;
    pageSize?: number;
    equipmentsCountLimit?: number;
    clientBranchId?: string;
    consumptionCreatedAtGteq?: string;
    consumptionCreatedAtLteq?: string;
  }
}

export const useBranchesEquipmentsConsumptionsTotalizer = ({
  filters: {
    page,
    pageSize,
    equipmentsCountLimit,
    clientBranchId,
    consumptionCreatedAtGteq,
    consumptionCreatedAtLteq,
  } = {},
}: UseBranchesEquipmentsConsumptionsTotalizerProps) => {
  const privateAxios = usePrivateAxios();

  const queryClient = useQueryClient();

  const mapper = useMemo(() => {
    return BranchesEquipmentsConsumptionsTotalizerTransformer.totalizerTransformer();
  }, []);

  const queryKey = [
    'clientCompanyBranchesEquipmentsConsumptionsTotalizer',
    page,
    pageSize,
    consumptionCreatedAtGteq,
    consumptionCreatedAtLteq,
  ];

  const queryFunction = useCallback((params: {
    equipmentsCountLimit?: number;
    clientBranchId?: string;
  }) => {
    return privateAxios.get(`/companies/mine/branches_equipments_consumptions_totalizer`, {
      params: {
        page,
        page_size: pageSize,
        equipments_count_limit: params.equipmentsCountLimit,
        'q[client_branch_id_eq]': params.clientBranchId,
        'q[consumption_created_at_gteq]': consumptionCreatedAtGteq,
        'q[consumption_created_at_lteq]': consumptionCreatedAtLteq,
      }
    });
  }, queryKey);

  const {
    data: { data: rawData, meta } = defaultTransformerResponse(),
    dataUpdatedAt,
    ...rest
  } = useQuery({
    queryKey,
    queryFn: () => queryFunction({
      equipmentsCountLimit,
      clientBranchId,
    }),
    select: (data: { data: { data: any[], meta: MetaResponse; } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      };
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: fetchAllBranchEquipments,
  } = useMutation({
    mutationFn: (params: {
      clientBranchId: string;
      equipmentsCountLimit: number;
    }) => {
      return queryFunction(params);
    },
    onSuccess: (response, params) => {
      const rawResponseData = (response?.data?.data || []) as BranchesEquipmentsConsumptionsTotalizerData[];

      queryClient.setQueryData(queryKey, (oldCache: { data: { data: any[]; meta: MetaResponse } } | undefined) => {
        if (!oldCache) return oldCache;

        const newData = [...oldCache.data.data];

        const branchIndex = newData.findIndex((row) => row.branch_id === params.clientBranchId);

        if (branchIndex === -1) return oldCache;

        const rowsOfBranch = newData.filter((row) => row.branch_id === params.clientBranchId).length;

        newData.splice(branchIndex, rowsOfBranch, ...rawResponseData);

        return {
          ...oldCache,
          data: {
            ...oldCache.data,
            data: newData,
          },
        };
      });
    },
  });

  return { data: rawData, meta, dataUpdatedAt, ...rest, fetchAllBranchEquipments };
}
